import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Terminal,
  Search as SearchIcon,
  XCircle,
  X,
  Copy,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  Info,
  Activity,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Layers,
} from "lucide-react";
import Chart from "react-apexcharts";
import baseapi from "../../../services/baseapi";
import { showError, showSuccess, showInfo } from "../../../utils/toast";
import { jwt_decode } from "../../../utils/jwt_decode";
import { Pagination } from "../../InternalScan/InternalScanDashboard/Pagination";

interface SiemensLog {
  agent_id?: number;
  asset_id?: number;
  channel?: string;
  client_id?: number;
  created?: string;
  db_log?: boolean;
  error_code?: string | number;
  event_id?: string | number;
  level?: string;
  log_timestamp?: string;
  message?: string | any;
  organization_id?: number;
  platform?: string;
  raw_data?: any;
  source_id?: string;
  source_type?: string;
  _id?: string;
}

const formatLogMessage = (msg: any): string => {
  if (msg === null || msg === undefined) return "";
  if (typeof msg === "string") return msg.replace(/[ \t]+/g, " ");
  try {
    return JSON.stringify(msg).replace(/[ \t]+/g, " ");
  } catch {
    return String(msg);
  }
};

interface LogMonitorViewProps {
  activeCluster: string;
  clusterName?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export const LogMonitorView: React.FC<LogMonitorViewProps> = ({
  activeCluster,
  clusterName,
  clientId,
  refreshKey,
}) => {
  const effectiveSourceId = clusterName || activeCluster || "";

  // Logs Data States
  const [logs, setLogs] = useState<SiemensLog[]>([]);
  const [totalLogs, setTotalLogs] = useState<number>(0);
  const [levelsStats, setLevelsStats] = useState<Record<string, number>>({});

  // Loading States
  const [loadingLogs, setLoadingLogs] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Filters and Visualization States
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [isChartCollapsed, setIsChartCollapsed] = useState<boolean>(false);

  // Pagination & Search
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(200);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Row expansion state
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const searchDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch cluster-wide severity levels stats
  const fetchLevelsStats = useCallback(async () => {
    if (!effectiveSourceId) {
      setLevelsStats({});
      return;
    }

    const params: any = {
      db_log: true,
      source_id: effectiveSourceId,
    };

    if (clientId) params.client_id = clientId;
    const decodedToken = jwt_decode();
    if (decodedToken?.organization_id) {
      params.organization_id = decodedToken.organization_id;
    }

    try {
      const response = await baseapi.get("/siem_logs/levels", { params });
      if (response.data?.levels && Array.isArray(response.data.levels)) {
        const statsMap: Record<string, number> = {};
        response.data.levels.forEach((lvl: any) => {
          if (lvl.level) {
            statsMap[lvl.level.toLowerCase()] = Number(lvl.count) || 0;
          }
        });
        setLevelsStats(statsMap);
      }
    } catch {
      // Graceful fallback to client-side computation from logs buffer
    }
  }, [effectiveSourceId, clientId]);

  // Fetch Logs
  const fetchLogs = useCallback(async () => {
    if (!effectiveSourceId) {
      setLogs([]);
      setTotalLogs(0);
      return;
    }

    setLoadingLogs(true);
    const params: any = {
      page: currentPage,
      page_size: pageSize,
      db_log: true,
      source_id: effectiveSourceId,
    };

    if (clientId) {
      params.client_id = clientId;
    }

    const decodedToken = jwt_decode();
    if (decodedToken?.organization_id) {
      params.organization_id = decodedToken.organization_id;
    }

    if (searchQuery) {
      params.search = searchQuery;
    }

    if (selectedLevel) {
      params.level = selectedLevel;
    }

    if (selectedChannel) {
      params.channel = selectedChannel;
    }

    try {
      const response = await baseapi.get("/siem_logs/search", { params });
      const logsData =
        response.data?.logs ||
        response.data?.results ||
        response.data?.data ||
        (Array.isArray(response.data) ? response.data : null);

      if (Array.isArray(logsData)) {
        setLogs(logsData);
        setTotalLogs(
          typeof response.data?.total === "number"
            ? response.data.total
            : logsData.length
        );
      } else {
        setLogs([]);
        setTotalLogs(0);
      }
      setExpandedRows({});
    } catch (error: any) {
      showError(error.message || "Failed to fetch logs");
      setLogs([]);
      setTotalLogs(0);
    } finally {
      setLoadingLogs(false);
    }
  }, [effectiveSourceId, clientId, currentPage, pageSize, searchQuery, selectedLevel, selectedChannel]);

  // Load stats on dependency changes
  useEffect(() => {
    fetchLevelsStats();
  }, [fetchLevelsStats, refreshKey]);

  // Load logs on dependency changes
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, refreshKey]);

  // Handle Search Input Change with Debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchInput(val);

    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);

    searchDebounceTimer.current = setTimeout(() => {
      setSearchQuery(val.trim());
      setCurrentPage(1);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    };
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchLogs(), fetchLevelsStats()]);
    setIsRefreshing(false);
  };

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Timestamp Formatter
  const formatTerminalTime = (ts?: string) => {
    if (!ts) return "-";
    try {
      const date = new Date(ts);
      const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];
      const month = months[date.getMonth()];
      const day = date.getDate().toString().padStart(2, "0");
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const seconds = date.getSeconds().toString().padStart(2, "0");
      return `${day}/${month} ${hours}:${minutes}:${seconds}`;
    } catch {
      return `${ts}`;
    }
  };

  // Severity Level Badge
  const getTerminalLevel = (level?: string) => {
    const l = (level || "").toLowerCase();
    if (l.includes("critical") || l.includes("fatal")) return <span className="text-red-500 font-bold">CRITICAL</span>;
    if (l.includes("error") || l.includes("err")) return <span className="text-red-500 font-bold">ERROR</span>;
    if (l.includes("warn")) return <span className="text-yellow-500 font-bold">WARNING</span>;
    if (l.includes("debug")) return <span className="text-blue-400 font-bold">DEBUG</span>;
    return <span className="text-cyan-400 font-bold">INFO</span>;
  };

  // Compute Datadog Analytics & Histogram Metrics
  const {
    errorCount,
    warningCount,
    infoCount,
    errorRate,
    warningRate,
    availableChannels,
    timelineBuckets,
    histogramSeries,
    histogramOptions,
  } = useMemo(() => {
    let err = 0;
    let warn = 0;
    let inf = 0;
    let oth = 0;

    const channelMap: Record<string, number> = {};
    const validTimestamps: { time: number; level: string }[] = [];

    logs.forEach((log) => {
      const l = (log.level || "").toLowerCase();
      if (l.includes("crit") || l.includes("err") || l.includes("fatal")) {
        err++;
      } else if (l.includes("warn")) {
        warn++;
      } else if (l.includes("info")) {
        inf++;
      } else {
        oth++;
      }

      if (log.channel) {
        channelMap[log.channel] = (channelMap[log.channel] || 0) + 1;
      }

      const ts = log.log_timestamp || log.created;
      if (ts) {
        const parsed = new Date(ts).getTime();
        if (!isNaN(parsed)) {
          validTimestamps.push({ time: parsed, level: l });
        }
      }
    });

    const totalC = totalLogs || logs.length;
    const computedErr = (!selectedLevel && levelsStats.error !== undefined)
      ? ((levelsStats.error || 0) + (levelsStats.critical || 0))
      : err;
    const computedWarn = (!selectedLevel && levelsStats.warning !== undefined)
      ? (levelsStats.warning || 0)
      : warn;
    const computedInf = (!selectedLevel && levelsStats.info !== undefined)
      ? (levelsStats.info || 0)
      : inf;

    const eRate = totalC > 0 ? ((computedErr / totalC) * 100).toFixed(1) : "0.0";
    const wRate = totalC > 0 ? ((computedWarn / totalC) * 100).toFixed(1) : "0.0";

    const channels = Object.entries(channelMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    // Build timeline buckets (Datadog style)
    const numBuckets = 18;
    const buckets: { label: string; error: number; warning: number; info: number; other: number }[] = [];

    if (validTimestamps.length > 0) {
      validTimestamps.sort((a, b) => a.time - b.time);
      const minT = validTimestamps[0].time;
      const maxT = validTimestamps[validTimestamps.length - 1].time;
      const timeSpan = Math.max(maxT - minT, 60000 * 15);
      const bucketSize = timeSpan / numBuckets;

      for (let i = 0; i < numBuckets; i++) {
        const bStart = minT + i * bucketSize;
        const d = new Date(bStart);
        const hours = d.getHours().toString().padStart(2, "0");
        const mins = d.getMinutes().toString().padStart(2, "0");
        buckets.push({
          label: `${hours}:${mins}`,
          error: 0,
          warning: 0,
          info: 0,
          other: 0,
        });
      }

      validTimestamps.forEach(({ time, level }) => {
        let bIdx = Math.floor((time - minT) / bucketSize);
        if (bIdx >= numBuckets) bIdx = numBuckets - 1;
        if (bIdx < 0) bIdx = 0;

        if (level.includes("crit") || level.includes("err") || level.includes("fatal")) {
          buckets[bIdx].error++;
        } else if (level.includes("warn")) {
          buckets[bIdx].warning++;
        } else if (level.includes("info")) {
          buckets[bIdx].info++;
        } else {
          buckets[bIdx].other++;
        }
      });
    }

    const series = [
      { name: "Critical & Error", data: buckets.map((b) => b.error) },
      { name: "Warning", data: buckets.map((b) => b.warning) },
      { name: "Info", data: buckets.map((b) => b.info) },
      { name: "Other", data: buckets.map((b) => b.other) },
    ];

    const options: any = {
      chart: {
        type: "bar",
        stacked: true,
        height: 140,
        toolbar: { show: false },
        background: "transparent",
        animations: { enabled: true, speed: 300 },
        sparkline: { enabled: false },
      },
      colors: ["#ef4444", "#f59e0b", "#06b6d4", "#64748b"],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "60%",
          borderRadius: 2,
        },
      },
      dataLabels: { enabled: false },
      stroke: { width: 0 },
      xaxis: {
        categories: buckets.map((b) => b.label),
        labels: {
          style: {
            colors: "#9ca3af",
            fontSize: "10px",
            fontFamily: "monospace",
          },
          rotate: -20,
          hideOverlappingLabels: true,
        },
        axisBorder: { color: "#27272a" },
        axisTicks: { color: "#27272a" },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#9ca3af",
            fontSize: "10px",
            fontFamily: "monospace",
          },
        },
        tickAmount: 3,
      },
      grid: {
        borderColor: "#1f2937",
        strokeDashArray: 3,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
        padding: { top: 0, right: 10, bottom: 0, left: 10 },
      },
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "right",
        labels: { colors: "#d1d5db" },
        fontSize: "10px",
        markers: { radius: 3 },
        itemMargin: { horizontal: 6, vertical: 0 },
      },
      tooltip: {
        theme: "dark",
        shared: true,
        intersect: false,
        style: {
          fontSize: "11px",
          fontFamily: "monospace",
        },
        y: {
          formatter: (val: number) => `${val} logs`,
        },
      },
    };

    return {
      errorCount: computedErr,
      warningCount: computedWarn,
      infoCount: computedInf,
      otherCount: oth,
      errorRate: eRate,
      warningRate: wRate,
      availableChannels: channels,
      timelineBuckets: buckets,
      histogramSeries: series,
      histogramOptions: options,
    };
  }, [logs, totalLogs, levelsStats, selectedLevel]);

  // Client-side channel filtering if needed
  const displayedLogs = useMemo(() => {
    if (!selectedChannel) return logs;
    return logs.filter((l) => l.channel === selectedChannel);
  }, [logs, selectedChannel]);

  // Copy Logs to Clipboard
  const handleCopyLogs = () => {
    if (displayedLogs.length === 0) {
      showInfo("No logs to copy");
      return;
    }

    const getTerminalLevelText = (level?: string) => {
      const l = (level || "").toLowerCase();
      if (l.includes("critical") || l.includes("fatal")) return "CRITICAL";
      if (l.includes("error") || l.includes("err")) return "ERROR";
      if (l.includes("warn")) return "WARNING";
      if (l.includes("debug")) return "DEBUG";
      return "INFO";
    };

    const logsText = displayedLogs
      .map((log) => {
        const timeStr = formatTerminalTime(log.log_timestamp || log.created);
        const levelStr = getTerminalLevelText(log.level);
        const messageStr = formatLogMessage(log.message);
        return `${timeStr}: ${levelStr}: ${messageStr}`;
      })
      .join("\n");

    navigator.clipboard
      .writeText(logsText)
      .then(() => {
        showSuccess("Logs copied to clipboard");
      })
      .catch(() => {
        showError("Failed to copy logs");
      });
  };

  const totalPages = Math.max(1, Math.ceil(totalLogs / pageSize));

  return (
    <div className="space-y-3 relative flex flex-col h-full [color-scheme:dark]">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-800 pb-3 shrink-0">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2 tracking-wide">
            <div className="p-1.5 rounded bg-orange-500 text-white">
              <Terminal className="w-4 h-4 text-white" />
            </div>
            Database Log Monitor
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Real-time database engine audit, operational &amp; severity log stream
            {effectiveSourceId && (
              <span className="ml-1 text-orange-400 font-mono font-semibold">
                ({effectiveSourceId})
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={loadingLogs || isRefreshing}
            className="p-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5 text-xs font-semibold cursor-pointer"
            title="Refresh logs"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin text-orange-400" : ""}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* ─── DATADOG-STYLE TOP LOG ANALYTICS & HISTOGRAM ROW ─── */}
      <div className="space-y-2.5 shrink-0">
        {/* Row 1: KPI Stat Cards (Compact & Compressed) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          {/* Card 1: Total Logs */}
          <div
            onClick={() => {
              setSelectedLevel(null);
              setSelectedChannel(null);
              setCurrentPage(1);
            }}
            className={`py-2 px-3 rounded-lg border transition-all cursor-pointer select-none flex items-center justify-between ${
              selectedLevel === null && selectedChannel === null
                ? "bg-slate-900/90 border-green-500/50 shadow-md ring-1 ring-green-500/30"
                : "bg-[#111827]/80 hover:bg-[#182234] border-gray-800 hover:border-gray-700"
            }`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400 shrink-0">
                <Activity className="w-3.5 h-3.5" />
              </div>
              <div className="truncate">
                <div className="text-[11px] font-bold text-gray-300">Total Logs</div>
                <div className="text-base font-extrabold text-white font-mono leading-tight">
                  {totalLogs.toLocaleString()}
                </div>
              </div>
            </div>
            {selectedLevel === null && selectedChannel === null ? (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 border border-green-500/30 font-mono shrink-0">
                ALL
              </span>
            ) : (
              <span className="text-[10px] text-gray-500 font-mono shrink-0">Reset</span>
            )}
          </div>

          {/* Card 2: Critical & Errors */}
          <div
            onClick={() => {
              setSelectedLevel((prev) => (prev === "error" ? null : "error"));
              setCurrentPage(1);
            }}
            className={`py-2 px-3 rounded-lg border transition-all cursor-pointer select-none flex items-center justify-between ${
              selectedLevel === "error" || selectedLevel === "critical"
                ? "bg-red-950/30 border-red-500/60 shadow-md ring-1 ring-red-500/40"
                : "bg-[#111827]/80 hover:bg-[#1f1619] border-gray-800 hover:border-red-900/40"
            }`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 shrink-0">
                <AlertCircle className="w-3.5 h-3.5" />
              </div>
              <div className="truncate">
                <div className="text-[11px] font-bold text-red-400">Errors &amp; Critical</div>
                <div className="text-base font-extrabold text-red-400 font-mono leading-tight">
                  {errorCount.toLocaleString()}
                </div>
              </div>
            </div>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20 font-mono shrink-0">
              {errorRate}% rate
            </span>
          </div>

          {/* Card 3: Warnings */}
          <div
            onClick={() => {
              setSelectedLevel((prev) => (prev === "warning" ? null : "warning"));
              setCurrentPage(1);
            }}
            className={`py-2 px-3 rounded-lg border transition-all cursor-pointer select-none flex items-center justify-between ${
              selectedLevel === "warning"
                ? "bg-amber-950/30 border-amber-500/60 shadow-md ring-1 ring-amber-500/40"
                : "bg-[#111827]/80 hover:bg-[#221c13] border-gray-800 hover:border-amber-900/40"
            }`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 shrink-0">
                <AlertTriangle className="w-3.5 h-3.5" />
              </div>
              <div className="truncate">
                <div className="text-[11px] font-bold text-amber-400">Warnings</div>
                <div className="text-base font-extrabold text-amber-400 font-mono leading-tight">
                  {warningCount.toLocaleString()}
                </div>
              </div>
            </div>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 font-mono shrink-0">
              {warningRate}%
            </span>
          </div>

          {/* Card 4: Info & Operational */}
          <div
            onClick={() => {
              setSelectedLevel((prev) => (prev === "info" ? null : "info"));
              setCurrentPage(1);
            }}
            className={`py-2 px-3 rounded-lg border transition-all cursor-pointer select-none flex items-center justify-between ${
              selectedLevel === "info"
                ? "bg-cyan-950/30 border-cyan-500/60 shadow-md ring-1 ring-cyan-500/40"
                : "bg-[#111827]/80 hover:bg-[#132026] border-gray-800 hover:border-cyan-900/40"
            }`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 shrink-0">
                <Info className="w-3.5 h-3.5" />
              </div>
              <div className="truncate">
                <div className="text-[11px] font-bold text-cyan-400">Info &amp; Routine</div>
                <div className="text-base font-extrabold text-cyan-400 font-mono leading-tight">
                  {infoCount.toLocaleString()}
                </div>
              </div>
            </div>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-mono shrink-0">
              Healthy
            </span>
          </div>
        </div>

        {/* Row 2: Datadog Log Volume Stacked Timeline Histogram */}
        <div className="bg-[#111827]/90 border border-gray-800 rounded-xl p-3 shadow-lg">
          <div className="flex items-center justify-between pb-2 border-b border-gray-800/80 mb-2">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-orange-400" />
              <span className="text-xs font-bold text-white tracking-wide">
                Log Volume &amp; Severity Timeline Histogram
              </span>
              <span className="text-[10px] text-gray-500 font-mono hidden sm:inline">
                (Time-series distribution)
              </span>
            </div>

            <div className="flex items-center gap-2">
              {selectedLevel && (
                <button
                  onClick={() => {
                    setSelectedLevel(null);
                    setCurrentPage(1);
                  }}
                  className="text-[10px] font-bold text-orange-400 hover:text-orange-300 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 px-2 py-0.5 rounded transition-colors flex items-center gap-1 cursor-pointer"
                >
                  <span>Level: {selectedLevel.toUpperCase()}</span>
                  <X className="w-3 h-3" />
                </button>
              )}
              {selectedChannel && (
                <button
                  onClick={() => {
                    setSelectedChannel(null);
                    setCurrentPage(1);
                  }}
                  className="text-[10px] font-bold text-orange-400 hover:text-orange-300 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 px-2 py-0.5 rounded transition-colors flex items-center gap-1 cursor-pointer"
                >
                  <span>Channel: {selectedChannel}</span>
                  <X className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={() => setIsChartCollapsed(!isChartCollapsed)}
                className="text-gray-400 hover:text-white p-1 hover:bg-gray-800 rounded transition-colors text-xs flex items-center gap-1 cursor-pointer"
                title={isChartCollapsed ? "Expand Histogram" : "Collapse Histogram"}
              >
                {isChartCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
                <span className="text-[10px] font-medium">{isChartCollapsed ? "Show Chart" : "Hide Chart"}</span>
              </button>
            </div>
          </div>

          {!isChartCollapsed && (
            <div className="w-full h-[145px]">
              {timelineBuckets.length > 0 ? (
                <Chart
                  options={histogramOptions}
                  series={histogramSeries}
                  type="bar"
                  height={140}
                  width="100%"
                />
              ) : (
                <div className="h-[140px] flex items-center justify-center text-xs text-gray-500 font-mono">
                  {loadingLogs ? "Loading log timeline..." : "No timestamped log events available"}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Linux Terminal styled container */}
      <div className="flex-1 flex flex-col min-h-[500px] bg-black rounded-xl border border-gray-800 shadow-2xl overflow-hidden font-mono">
        {/* Terminal Header */}
        <div className="bg-[#181a1f] px-4 py-2 border-b border-zinc-850 flex items-center justify-between shrink-0 select-none">
          <div className="flex items-center space-x-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
            <span className="text-[10px] text-gray-400 pl-2 flex items-center gap-1.5 font-mono">
              <Terminal size={11} className="text-green-500 animate-pulse" />
              logs@db-monitor:~/{effectiveSourceId ? `${effectiveSourceId}.log` : "database.log"}
            </span>
          </div>
          <div className="text-[9px] text-gray-500 font-mono select-none flex items-center gap-2">
            {selectedLevel && (
              <span className="text-orange-400 font-bold">
                filter:[{selectedLevel}]
              </span>
            )}
            {selectedChannel && (
              <span className="text-cyan-400 font-bold">
                channel:[{selectedChannel}]
              </span>
            )}
            <span>lines: {displayedLogs.length}</span>
            <span>| total: {totalLogs.toLocaleString()}</span>
          </div>
        </div>

        {/* Log Filter Inputs & Channels Bar */}
        <div className="p-2.5 border-b border-gray-900 flex flex-wrap items-center justify-between gap-3 shrink-0 bg-zinc-950/50">
          <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
            {/* Search Input */}
            <div className="relative w-full sm:w-64 shrink-0">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input
                type="text"
                value={searchInput}
                onChange={handleSearchChange}
                placeholder="Search logs..."
                className="w-full bg-zinc-900/80 border border-zinc-800 rounded-lg pl-8 pr-7 py-1 text-xs text-gray-200 placeholder-gray-500 focus:outline-none focus:border-green-500 font-mono transition-colors"
              />
              {searchInput && (
                <button
                  onClick={() => {
                    setSearchInput("");
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  <X size={11} />
                </button>
              )}
            </div>

            {/* Channels Pill Selector (Professional Positioning) */}
            {availableChannels.length > 0 && (
              <div className="flex items-center gap-1.5 overflow-x-auto custom-scroll py-0.5 max-w-full">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider shrink-0 flex items-center gap-1 font-mono">
                  <Layers className="w-3 h-3 text-orange-400" /> Channel:
                </span>
                <button
                  onClick={() => {
                    setSelectedChannel(null);
                    setCurrentPage(1);
                  }}
                  className={`px-2 py-0.5 rounded text-[10px] font-mono font-semibold transition-all shrink-0 cursor-pointer ${
                    selectedChannel === null
                      ? "bg-orange-500/20 border border-orange-500/40 text-orange-300 shadow-sm"
                      : "bg-zinc-900 text-gray-400 hover:text-gray-200 border border-zinc-800 hover:border-zinc-700"
                  }`}
                >
                  All
                </button>
                {availableChannels.map((ch) => (
                  <button
                    key={ch.name}
                    onClick={() => {
                      setSelectedChannel((prev) => (prev === ch.name ? null : ch.name));
                      setCurrentPage(1);
                    }}
                    className={`px-2 py-0.5 rounded text-[10px] font-mono font-semibold transition-all shrink-0 flex items-center gap-1.5 cursor-pointer ${
                      selectedChannel === ch.name
                        ? "bg-orange-500/20 border border-orange-500/40 text-orange-300 shadow-sm"
                        : "bg-zinc-900 text-gray-400 hover:text-gray-200 border border-zinc-800 hover:border-zinc-700"
                    }`}
                  >
                    <span>{ch.name}</span>
                    <span className="px-1 py-0.2 rounded bg-black/50 text-[9px] text-gray-400 font-mono">
                      {ch.count}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Action: Copy Logs */}
          <div className="flex items-center gap-2 select-none shrink-0">
            <button
              onClick={handleCopyLogs}
              disabled={displayedLogs.length === 0 || loadingLogs}
              className="py-1 px-2.5 bg-zinc-900 hover:bg-zinc-800 text-gray-300 border border-zinc-800 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 text-[10px] font-mono hover:text-green-400 font-bold cursor-pointer"
              title="Copy terminal view to clipboard"
            >
              <Copy className="w-3 h-3" />
              <span>Copy Logs</span>
            </button>
          </div>
        </div>

        {/* Scrollable logs list container */}
        <div className="flex-1 overflow-x-auto overflow-y-auto custom-scroll min-h-0 bg-black p-3 space-y-1.5 font-mono">
          {loadingLogs ? (
            <div className="py-24 text-center">
              <div className="flex flex-col items-center justify-center space-y-3">
                <div className="w-6 h-6 border-2 border-green-500/20 border-t-green-500 rounded-full animate-spin"></div>
                <span className="text-[10px] font-black uppercase tracking-widest text-green-500 animate-pulse">
                  Running query...
                </span>
              </div>
            </div>
          ) : displayedLogs.length === 0 ? (
            <div className="py-24 text-center">
              <div className="flex flex-col items-center justify-center space-y-2 text-zinc-650">
                <XCircle size={32} className="text-zinc-800 mb-1" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  No matching logs found in shell buffer
                </p>
                {effectiveSourceId && (
                  <p className="text-[10px] text-zinc-600 font-mono">
                    Cluster source: {effectiveSourceId}
                  </p>
                )}
                {(selectedLevel || selectedChannel) && (
                  <button
                    onClick={() => {
                      setSelectedLevel(null);
                      setSelectedChannel(null);
                      setCurrentPage(1);
                    }}
                    className="mt-2 px-3 py-1 bg-gray-800 hover:bg-gray-700 text-orange-400 rounded text-xs font-semibold cursor-pointer border border-gray-700"
                  >
                    Clear Active Filters
                  </button>
                )}
              </div>
            </div>
          ) : (
            displayedLogs.map((log, idx) => {
              const isExpanded = !!expandedRows[idx];

              return (
                <React.Fragment key={idx}>
                  <div
                    onClick={() => toggleRow(idx)}
                    className="hover:bg-zinc-900/60 p-1 rounded transition-colors cursor-pointer text-xs leading-relaxed flex items-start"
                  >
                    <span className="text-amber-500/95 shrink-0 select-none mr-2">
                      {formatTerminalTime(log.log_timestamp || log.created)}:
                    </span>
                    <span className="shrink-0 select-none mr-2">
                      {getTerminalLevel(log.level)}:
                    </span>
                    <span className="text-green-400/90 font-mono break-all whitespace-pre-wrap flex-1">
                      {formatLogMessage(log.message)}
                    </span>
                  </div>

                  {/* Expanded Details Row */}
                  {isExpanded && (
                    <div className="bg-zinc-950/80 px-4 py-3 border border-zinc-900 rounded-lg my-1.5 font-mono">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px] border-b border-zinc-900 pb-3 text-zinc-400">
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-green-600 uppercase tracking-wider">
                            Channel
                          </span>
                          <span className="text-zinc-300 font-mono break-all mt-0.5 bg-black/50 px-1.5 py-0.5 border border-zinc-900 rounded">
                            {log.channel || "-"}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-green-600 uppercase tracking-wider">
                            Source Type
                          </span>
                          <span className="text-zinc-300 mt-0.5 font-semibold uppercase">
                            {log.source_type || "-"}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-green-600 uppercase tracking-wider">
                            Source ID
                          </span>
                          <span className="text-zinc-300 font-mono break-all mt-0.5 bg-black/50 px-1.5 py-0.5 border border-zinc-900 rounded">
                            {log.source_id || "-"}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-green-600 uppercase tracking-wider">
                            {log.error_code ? "Event / Error Code" : "Event ID"}
                          </span>
                          <span className="text-zinc-300 font-mono mt-0.5">
                            {log.event_id || log.error_code || "-"}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col mt-3">
                        <span className="text-[8px] font-bold text-green-600 uppercase tracking-wider mb-1">
                          Full Log Entry
                        </span>
                        <pre className="bg-black border border-zinc-900 rounded-lg p-3 text-[11px] font-mono text-green-400/90 leading-relaxed overflow-x-auto whitespace-pre-wrap break-all shadow-inner">
                          {(() => {
                            if (typeof log.message === "object") {
                              return JSON.stringify(log.message, null, 2);
                            }
                            try {
                              const parsed = JSON.parse(log.message || "");
                              return JSON.stringify(parsed, null, 2);
                            } catch {
                              return log.message || "-";
                            }
                          })()}
                        </pre>
                      </div>

                      {log.raw_data && (
                        <div className="flex flex-col mt-3">
                          <span className="text-[8px] font-bold text-green-600 uppercase tracking-wider mb-1">
                            Raw Payload Details
                          </span>
                          <pre className="bg-black border border-zinc-900 rounded-lg p-3 text-[10px] font-mono text-zinc-300 overflow-x-auto shadow-inner">
                            {JSON.stringify(log.raw_data, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })
          )}
        </div>

        {/* Pagination Footer */}
        <div className="shrink-0 border-t border-zinc-900 bg-zinc-950/80">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={pageSize}
            totalItems={totalLogs}
            onItemsPerPageChange={(val) => {
              setPageSize(val);
              setCurrentPage(1);
            }}
            itemsPerPageOptions={[50, 100, 200, 300, 400, 500]}
          />
        </div>
      </div>
    </div>
  );
};

export default LogMonitorView;
